import React, { useEffect } from 'react';
import ArticlesSerivce from '../../../Services/Articles';
import { useLocation, useNavigate } from 'react-router-dom';
import { HeaderBox } from '../mBookListPage';
import { Box, CircularProgress, IconButton, Typography, Paper, Snackbar, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDownloadApp } from '../../../Contexts/DownloadAppContext';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import TextFormatIcon from '@mui/icons-material/TextFormat';
import Favfill from "../../../assets/img/favadd.svg";

const ReadArticles = () => {
    const location: any = useLocation();
    const articleId :any = location.state?.articleId;
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { setShowDownloadPopup } = useDownloadApp();

    const [articleData, setArticleData] = React.useState<any>(null);
    const [loading, setLoading] = React.useState(true);
    const [fontSize, setFontSize] = React.useState(16);
    const [isLiked, setIsLiked] = React.useState(false);

    // Snackbar state
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState('');
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<'success' | 'error' | 'info' | 'warning'>('info');

    
    useEffect(() => {
        const userId = localStorage.getItem("UserId") || "";
        const lang = localStorage.getItem("lan");

        if (articleId && lang) {

            ArticlesSerivce.getArticleById(articleId, userId, lang)
                .then((response: any) => {
                    if (response && response.result) {
                        console.log("Article Data:", response.result);
                        setArticleData(response.result);
                        setIsLiked(response.result.isFavourite);
                    }
                })
                .catch((error: any) => {
                    console.error("Error fetching article:", error);
                    showSnackbar("Failed to load article.", 'error');
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            setLoading(false);
        }
    }, [articleId]);

    const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    // Keep stripHtmlTags for copy and share, as they need plain text
    const stripHtmlTags = (htmlString: any) => {
        if (!htmlString) return "";
        const doc = new DOMParser().parseFromString(htmlString, 'text/html');
        return doc.body.textContent || "";
    };

    const handleDecreaseFontSize = () => {
        setFontSize(prevSize => Math.max(12, prevSize - 2));
    };

    const handleIncreaseFontSize = () => {
        setFontSize(prevSize => Math.min(24, prevSize + 2));
    };

    const handleCopyClick = () => {
        if (articleData?.articleContent) {
            const plainText = stripHtmlTags(articleData.articleContent);
            navigator.clipboard.writeText(plainText)
                .then(() => showSnackbar("Article content copied to clipboard!", 'success'))
                .catch(err => {
                    console.error("Could not copy text: ", err);
                    showSnackbar("Failed to copy content.", 'error');
                });
        }
    };

    const handleShareClick = () => {
        console.log("Share clicked!");
        if (navigator.share && articleData?.name && articleData?.articleContent) {
            navigator.share({
                title: articleData.name,
                text: stripHtmlTags(articleData.articleContent).substring(0, 150) + '...',
                url: window.location.href,
            }).catch((error) => {
                console.log('Error sharing', error);
                showSnackbar("Failed to share article.", 'error');
            });
        } else {
            showSnackbar("Web Share API is not supported in this browser. You can manually copy the content.", 'info');
        }
    };

    const handleWhatsAppClick = () => {
        console.log("WhatsApp clicked!");
        const articleTitle = articleData?.name || "Check out this article!";
        const articleLink = window.location.href;
        const whatsappMessage = `${articleTitle}: ${articleLink}`;
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`;
        window.open(whatsappUrl, '_blank');
    };

    const toggleFavourite = () => {
        if (!articleData?.id) {
            console.error("Article ID is not available to toggle favorite.");
            showSnackbar("Article ID is missing.", 'error');
            return;
        }

        if (isLiked) {
            ArticlesSerivce.removeArticlesFaviourite(articleData.id)
                .then(response => {
                    if (response && response.result) {
                        console.log("Article removed from favorites:", response.result);
                        setIsLiked(false);
                        showSnackbar("Article removed from favorites!", 'success');
                    } else {
                        showSnackbar("Failed to remove article from favorites.", 'error');
                    }
                })
                .catch(error => {
                    console.error("Error removing article from favorites:", error);
                    showSnackbar("Failed to remove article from favorites.", 'error');
                });
        } else {
            ArticlesSerivce.addArticlesFavourite(articleData.id)
                .then(response => {
                    if (response && response.result) {
                        console.log("Article added to favorites:", response.result);
                        setIsLiked(true);
                        showSnackbar("Article added to favorites!", 'success');
                    } else {
                        showSnackbar("Failed to add article to favorites.", 'error');
                    }
                })
                .catch(error => {
                    console.error("Error adding article to favorites:", error);
                    showSnackbar("Failed to add article to favorites.", 'error');
                });
        }
    };

    const fixedBottomBarHeight = 80;
    const iconCircleSx = {
        width: 35,
        height: 35,
        borderRadius: '50%',
        border: '1px solid #ff9800',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };
    const iconStyleSx = {
        color: '#ff9800',
        fontSize: '1.2rem',
    };

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
                {/* Header Section */}
                <HeaderBox>
                    <IconButton onClick={() => navigate(-1)} sx={{ position: 'absolute', left: 16, top: 35, p: 0 }}>
                        <ArrowBackIosIcon sx={{ color: "white", fontSize: '18px' }} />
                    </IconButton>
                    <IconButton onClick={() => navigate("/")} sx={{ position: 'absolute', left: 48, top: 35, p: 0 }}>
                        <HomeOutlinedIcon sx={{ color: "white", fontSize: '24px' }} />
                    </IconButton>
                    <Typography variant="caption" sx={{ color: "#ffffffdd", opacity: 0.9, mb: 0.5 }}>
                        {t("shrihari_tr")}
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#ffffffdd", opacity: 0.9, fontSize: 15 }}>
                        {t("Read_Articles_tr")}
                    </Typography>
                </HeaderBox>

                {/* Article Title and Author Section */}
                <Box sx={{
                    backgroundColor: '#f0f0f0',
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '3px solid #d3d3d373',
                }}>
                    <Typography variant="body2" sx={{ color: '#555', textAlign: 'center', fontSize: 18, fontWeight: 600 }}>
                        {articleData ? articleData.name : ""}
                        <div style={{
                            border: "1px solid #555",
                            width: articleData ? `${articleData.name.length * 10}px` : '50%',
                            margin: '0 auto',
                            marginBottom: 8,
                        }}></div>
                    </Typography>
                    <h4 style={{ textAlign: 'center', color: '#555', fontWeight: 400, margin: '0 auto' }}>{articleData ? articleData.author : ""}</h4>
                </Box>

                {/* Main Article Content Area */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            backgroundColor: "whitesmoke",
                            flexGrow: 1,
                            overflowY: 'auto',
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                            padding: '8px',
                            paddingBottom: `${fixedBottomBarHeight + 16}px`,
                        }}
                    >
                        {/* RENDER HTML DIRECTLY HERE */}
                        <Typography
                            variant="body1"
                            sx={{ fontSize: `${fontSize}px` }}
                            dangerouslySetInnerHTML={{ __html: articleData?.articleContent }}
                        />
                    </Box>
                )}

                {/* Fixed Bottom Bar UI */}
                <Paper
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: '#efe5d5',
                        height: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 16px',
                        boxShadow: '0px -2px 10px rgba(0, 0, 0, 0.1)',
                        borderRadius: '25px 25px 0 0',
                        border: '1px solid #ccc',
                        borderBottom: 'none',
                    }}
                    elevation={3}
                >
                    {/* Font Size Adjustments */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#F5F5F5',
                            borderRadius: '25px',
                            padding: '2px 4px',
                            gap: '4px',
                        }}
                    >
                        <IconButton size="small" onClick={handleDecreaseFontSize} sx={{ color: '#ff9800' }}>
                            <RemoveIcon />
                        </IconButton>
                        <Box sx={{
                            color: '#ff9800',
                            fontWeight: 'bold',
                            fontSize: '1.0rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '2px',
                        }}>
                            <TextFormatIcon sx={{ fontSize: '1.0rem' }} />
                        </Box>
                        <IconButton size="small" onClick={handleIncreaseFontSize} sx={{ color: '#ff9800' }}>
                            <AddIcon />
                        </IconButton>
                    </Box>

                    {/* Action Icons */}
                    <Box sx={{ display: 'flex', gap: '10px' }}>
                        <Box sx={iconCircleSx}>
                            <IconButton onClick={() => setShowDownloadPopup(true)} sx={{ color: '#ff9800' }} size="small">
                                {isLiked ? <img src={Favfill} alt="Favorite Filled" /> : <FavoriteBorderIcon sx={iconStyleSx} />}
                            </IconButton>
                        </Box>
                        <Box sx={iconCircleSx}>
                            <IconButton onClick={() => setShowDownloadPopup(true)} sx={{ color: '#ff9800' }} size="small">
                                <ContentCopyIcon sx={iconStyleSx} />
                            </IconButton>
                        </Box>
                        <Box sx={iconCircleSx}>
                            <IconButton onClick={() => setShowDownloadPopup(true)} sx={{ color: '#ff9800' }} size="small">
                                <ShareIcon sx={iconStyleSx} />
                            </IconButton>
                        </Box>
                        <Box sx={iconCircleSx}>
                            <IconButton onClick={() => setShowDownloadPopup(true)} sx={{ color: '#ff9800' }} size="small">
                                <WhatsAppIcon sx={iconStyleSx} />
                            </IconButton>
                        </Box>
                    </Box>
                </Paper>

                {/* Snackbar Component */}
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={4000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </Box>
        </>
    );
};

export default ReadArticles;